import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { awayPhrase } from '../lib/people';
import { signOut } from '../lib/auth';
import { useTheme } from '../lib/theme';
import ViewAsBar from './ViewAsBar';
import StatusMenu from './StatusMenu';
import { useFeatures } from '../lib/features';
import { resetBadge, useBadgePref } from '../lib/appBadge';
import { askToNotify, notifyState } from '../lib/osNotify';
import { usePhotos } from '../lib/photos';
import Avatar from './Avatar';
import PhotoEditor from './PhotoEditor';
import './AccountMenu.css';

/**
 * Everything that is about you rather than about the page, under your name.
 *
 * The header had grown a row of separate controls - a theme button, a "View
 * as…" picker, a Sign out button - each competing for the same corner and each
 * meaning nothing to anybody who had not been told what it was. They are all
 * account settings, so they belong behind the one thing in that corner that
 * identifies the account: your name.
 *
 * What stays outside the menu is what you need to see without opening
 * anything: who you are, that you are an administrator, and that you are
 * currently looking at somebody else's view.
 */

/** Two letters at most, from whatever we were given to work with. */
function initials(name, email) {
  const source = (name || email || '').trim();
  if (!source) return '?';
  const words = source.replace(/@.*$/, '').split(/[\s._-]+/).filter(Boolean);
  if (words.length === 0) return source[0].toUpperCase();
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

/*
 * THE COUNT ON THE JARVIS ICON - what the number on the app's taskbar or Dock
 * icon, the tab's icon and the tab's title counts (lib/appBadge). A choice
 * for this device, kept in this browser.
 */
const BADGE_CHOICES = [
  { key: 'all', label: 'Everything', hint: 'Unread messages, plus everything waiting in the bell.' },
  { key: 'messages', label: 'Messages', hint: 'Unread messages only.' },
  { key: 'off', label: 'Off', hint: 'No number on the app icon or the browser tab.' }
];

/*
 * A Mac shows the Dock's number only for an app allowed to notify (Chrome
 * 152 on: the badge goes with the app's own notification permission). Offered
 * once, here, while the browser has not been asked - never on page load.
 */
const isMac = () => {
  try { return /Mac/i.test(navigator.platform || navigator.userAgent || '') && !('ontouchend' in document); } catch { return false; }
};

function IconCount() {
  const [pref, setPref] = useBadgePref();
  const [askable, setAskable] = useState(() => isMac() && notifyState() === 'default');
  const now = BADGE_CHOICES.find((c) => c.key === pref) || BADGE_CHOICES[0];
  return (
    <div className="am-count" role="group" aria-labelledby="am-count-label">
      <div className="am-count-head">
        <svg className="am-ico" viewBox="0 0 18 18" fill="none" stroke="currentColor"
             strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="2.2" y="4.2" width="11.6" height="11.6" rx="2.6" />
          <circle cx="13.6" cy="4.4" r="2.9" fill="currentColor" stroke="none" />
        </svg>
        <span className="am-label" id="am-count-label">Count on the Jarvis icon</span>
      </div>
      <div className="am-seg" role="radiogroup" aria-labelledby="am-count-label">
        {BADGE_CHOICES.map((c) => (
          <button key={c.key} type="button" role="radio" aria-checked={pref === c.key}
                  className={`am-seg-btn${pref === c.key ? ' is-on' : ''}`}
                  data-pref={c.key} onClick={() => setPref(c.key)}>
            {c.label}
          </button>
        ))}
      </div>
      <p className="am-count-hint">{now.hint}</p>
      {askable && pref !== 'off' && (
        <button type="button" className="am-count-ask"
                onClick={() => { askToNotify().finally(() => setAskable(notifyState() === 'default')); }}>
          Allow notifications so the Dock shows the number
        </button>
      )}
    </div>
  );
}

/*
 * `onSignOut` is optional and defaults to the Entra sign-out, which is right
 * for everybody who has an Entra account. A contractor does not: they signed
 * in with a code to their own address, and signing them out means forgetting
 * a token in this browser, not round-tripping to Microsoft. One menu, one
 * place that decides how the signing-out is done.
 */
export default function AccountMenu({ me, onSignOut }) {
  const [open, setOpen] = useState(false);
  const features = useFeatures();
  const featureOn = (key) => (features ? features.features[key] !== false : true);
  const [showViewAs, setShowViewAs] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(false);
  const { dark, toggle } = useTheme();
  // Profile photos (080): which key is mine and whether I may choose one.
  // `me` here has no key of its own - a contractor's has no id at all - so
  // the photo store, which asked the server who it is talking to, says.
  const photos = usePhotos();
  const photoMe = photos.me;
  const offerPhoto = Boolean(photoMe && (photoMe.may_upload || photoMe.version));
  const openPhoto = () => { setOpen(false); setEditingPhoto(true); };
  const ref = useRef(null);

  // Click-away and Escape. The picker this replaces had neither, so the next
  // click anywhere on the page went to closing a menu the user had forgotten
  // was open.
  /*
   * A PANEL THIS MENU OPENED, LIVING SOMEWHERE ELSE IN THE DOM.
   *
   * The emoji picker renders through createPortal into document.body, so it
   * is inside this menu on screen and nowhere near it in the tree -
   * ref.current.contains(e.target) is false for every click in it. The first
   * click on an emoji therefore closed the whole account menu and took the
   * picker down with it, which is why the picker "did not work": it was
   * working, and being dismissed before it could answer.
   *
   * So the test is "is this click inside the menu, OR inside something the
   * menu put on screen". Escape is the same story: the picker handles its own
   * and this would otherwise close the menu underneath it at the same moment.
   */
  const inOurs = (node) => Boolean(
    (ref.current && ref.current.contains(node))
    || (node && node.closest && node.closest('.ch-ep, .ch-ep-scrim')));
  const pickerOpen = () => Boolean(document.querySelector('.ch-ep'));

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => {
      if (!inOurs(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape' && !pickerOpen()) setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Reopening the menu should not reopen a list of forty people.
  useEffect(() => { if (!open) setShowViewAs(false); }, [open]);

  /*
   * PEOPLE (113), asked when the menu opens rather than on every page: an
   * approver's own absence ("Out of office - away until 5 November"), and
   * for a member of staff who owns a joiner or leaver checklist or has a
   * step on one, the way to it. Nothing at all before 113, or for a
   * contractor.
   */
  const [people, setPeople] = useState(null);
  const staffSignedIn = Boolean(me?.signedIn) && !me?.isContractor;
  useEffect(() => {
    if (!open || !staffSignedIn) return undefined;
    let live = true;
    axios.get(`${process.env.REACT_APP_API_URL}/people/me`)
      .then((r) => { if (live) setPeople(r.data && r.data.ready ? r.data : null); })
      .catch(() => { if (live) setPeople(null); });
    return () => { live = false; };
  }, [open, staffSignedIn]);
  const showAway = staffSignedIn && Boolean(me?.isApprover) && Boolean(people);
  const showChecklists = staffSignedIn && !me?.isAdmin && Boolean(people && (people.runs > 0 || people.steps > 0));

  const name = me?.name || me?.email || 'Account';

  return (
    <div className="am" ref={ref}>
      <button
        type="button"
        className="am-trigger"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
        title={me?.email || name}
      >
        <Avatar person={{ key: photoMe?.key, name: me?.name }} className="am-avatar" aria-hidden="true"
                text={initials(me?.name, me?.email)} />
        {/* Kept on the trigger rather than inside the menu. Which account you
            are acting as, and whether it is an administrator's, is not
            something anyone should have to open a menu to find out. */}
        <span className="signin-who">
          <span className="signin-name am-name">{name}</span>
          {me?.isAdmin && <span className="signin-badge">Admin</span>}
          {me?.viewedBy && (
            <span className="signin-badge is-viewing" title={`Signed in as ${me.viewedBy}`}>
              Viewing as
            </span>
          )}
        </span>
        <svg className="am-chev" viewBox="0 0 16 16" fill="none" stroke="currentColor"
             strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 6.5 8 10.5 12 6.5" />
        </svg>
      </button>

      {open && (
        <div className="am-menu" role="menu">
          <div className="am-head">
            {/* Your own circle, large: pressing it is the quickest way to
                change the photo in it. */}
            {offerPhoto ? (
              <button type="button" className="am-head-photo" onClick={openPhoto}
                      title={photoMe.version ? 'Change your photo' : 'Add a photo'}
                      aria-label={photoMe.version ? 'Change your photo' : 'Add a photo'}>
                <Avatar person={{ key: photoMe.key, name: me?.name }} className="am-avatar am-avatar-lg"
                        size={40} text={initials(me?.name, me?.email)} aria-hidden="true" />
                <span className="am-head-badge" aria-hidden="true">
                  <svg viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor"
                       strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 5.5A1.5 1.5 0 0 1 3.5 4h1.4l1-1.5h4.2l1 1.5h1.4A1.5 1.5 0 0 1 14 5.5v6A1.5 1.5 0 0 1 12.5 13h-9A1.5 1.5 0 0 1 2 11.5Z" />
                    <circle cx="8" cy="8.4" r="2.3" />
                  </svg>
                </span>
              </button>
            ) : (
              <Avatar person={{ key: photoMe?.key, name: me?.name }} className="am-avatar am-avatar-lg"
                      size={40} text={initials(me?.name, me?.email)} aria-hidden="true" />
            )}
            <span className="am-head-who">
              <span className="am-head-name">{name}</span>
              {me?.email && <span className="am-head-email">{me.email}</span>}
            </span>
          </div>

          <div className="am-sep" />

          {/* Your status, first in the menu and above the theme switch,
              because it is the only thing in here that other people see and
              the only one anybody opens this menu in a hurry to change. */}
          <StatusMenu onDone={() => setOpen(false)} />

          <div className="am-sep" />

          {offerPhoto && (
            <button type="button" className="am-item am-photo" role="menuitem" onClick={openPhoto}>
              <svg className="am-ico" viewBox="0 0 18 18" fill="none" stroke="currentColor"
                   strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="9" cy="9" r="7" />
                <circle cx="9" cy="7.4" r="2.4" />
                <path d="M4.4 14.1a5.4 5.4 0 0 1 9.2 0" />
              </svg>
              <span className="am-label">Your photo</span>
            </button>
          )}

          <button
            type="button"
            className="am-item"
            role="menuitemcheckbox"
            aria-checked={dark}
            onClick={toggle}
          >
            <svg className="am-ico" viewBox="0 0 18 18" fill="none" stroke="currentColor"
                 strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M15.2 10.6A6.6 6.6 0 0 1 7.4 2.8a6.6 6.6 0 1 0 7.8 7.8Z" />
            </svg>
            <span className="am-label">Dark mode</span>
            {/* A switch rather than a second icon button: in a list of things
                you do, this is the one thing that has a state, and it should
                look like it has one. */}
            <span className={`am-switch${dark ? ' is-on' : ''}`} aria-hidden="true">
              <span className="am-knob" />
            </span>
          </button>

          <IconCount />

          {/* What Jarvis tells you about, and how (117) - for everybody
              signed in by either door. */}
          <Link to="/account/notifications" className="am-item am-notifications" role="menuitem"
                onClick={() => setOpen(false)}>
            <svg className="am-ico" viewBox="0 0 18 18" fill="none" stroke="currentColor"
                 strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4.5 12.6V8.4a4.5 4.5 0 0 1 9 0v4.2l1.3 1.6H3.2Z" />
              <path d="M7.4 15.6a1.7 1.7 0 0 0 3.2 0" />
            </svg>
            <span className="am-label">Notifications</span>
          </Link>

          {me?.isAdmin && (
            <>
              <button
                type="button"
                className="am-item"
                aria-expanded={showViewAs}
                onClick={() => setShowViewAs((v) => !v)}
              >
                <svg className="am-ico" viewBox="0 0 18 18" fill="none" stroke="currentColor"
                     strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M1.4 9S4.2 3.8 9 3.8 16.6 9 16.6 9 13.8 14.2 9 14.2 1.4 9 1.4 9Z" />
                  <circle cx="9" cy="9" r="2.2" />
                </svg>
                <span className="am-label">View as…</span>
                <svg className={`am-chev am-chev-item${showViewAs ? ' is-open' : ''}`}
                     viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.9"
                     strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M4 6.5 8 10.5 12 6.5" />
                </svg>
              </button>
              {showViewAs && <ViewAsBar isAdmin mode="panel" />}
            </>
          )}

          {showAway && (
            <Link to="/people/away" className="am-item am-away" role="menuitem" onClick={() => setOpen(false)}>
              <svg className="am-ico" viewBox="0 0 18 18" fill="none" stroke="currentColor"
                   strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="2.4" y="3.4" width="13.2" height="12" rx="2" />
                <path d="M2.4 7.4h13.2M6 1.9v3M12 1.9v3M6.4 11.2l5.2 0" />
              </svg>
              <span className="am-label">
                Out of office
                {people.away && <span className="am-sublabel"> · {awayPhrase(people.away)}</span>}
              </span>
            </Link>
          )}
          {showChecklists && (
            <Link to="/people" className="am-item am-checklists" role="menuitem" onClick={() => setOpen(false)}>
              <svg className="am-ico" viewBox="0 0 18 18" fill="none" stroke="currentColor"
                   strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="6.6" cy="5.8" r="2.6" />
                <path d="M2.2 14.8c0-2.5 2-4.1 4.4-4.1 1.2 0 2.2.4 3 1" />
                <path d="m10.8 13.1 1.8 1.8 3.4-3.6" />
              </svg>
              <span className="am-label">
                Joiner &amp; leaver checklists
                {people.steps > 0 && <span className="am-sublabel"> · {people.steps} step{people.steps === 1 ? '' : 's'} for you</span>}
              </span>
            </Link>
          )}

          {/* Jarvis as an app on the phone and the desktop, and the files
              that go with it. Under the account for every role - contractor
              included - because installing is something you do for yourself,
              like the theme, and the page it opens says how. */}
          <Link to="/install" className="am-item" role="menuitem" onClick={() => setOpen(false)}>
            <svg className="am-ico" viewBox="0 0 18 18" fill="none" stroke="currentColor"
                 strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 2.4v8.4M5.6 7.6 9 11l3.4-3.4" />
              <path d="M2.6 12.2v2.2a1.2 1.2 0 0 0 1.2 1.2h10.4a1.2 1.2 0 0 0 1.2-1.2v-2.2" />
            </svg>
            <span className="am-label">Install Jarvis &amp; downloads</span>
          </Link>

          {/* The calls you were on that somebody recorded - yours, whatever
              your role, which is why it is under the account rather than in
              the bar. An administrator's view of everybody's is under
              Advanced. */}
          {/* Meetings, for staff and administrators - the people who
              organise them - beside the bar's own item, so it is reachable
              from a page whose bar is folded into the burger. A contractor
              reaches a meeting from the bell or the invitation instead. */}
          {/* Both only while the feature is on for this person (078,
              Settings -> Features). */}
          {me?.signedIn && !me?.isContractor && featureOn('meetings') && (
            <Link to="/meetings" className="am-item am-meetings" role="menuitem" onClick={() => setOpen(false)}>
              <svg className="am-ico" viewBox="0 0 18 18" fill="none" stroke="currentColor"
                   strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="2.4" y="3.4" width="13.2" height="12" rx="2" />
                <path d="M2.4 7.4h13.2M6 1.9v3M12 1.9v3" />
                <circle cx="9" cy="11.2" r="1.3" fill="currentColor" stroke="none" />
              </svg>
              <span className="am-label">Meetings</span>
            </Link>
          )}

          {featureOn('recording') && (
          <Link to="/recordings" className="am-item am-recordings" role="menuitem" onClick={() => setOpen(false)}>
            <svg className="am-ico" viewBox="0 0 18 18" fill="none" stroke="currentColor"
                 strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="6.4" y="1.9" width="5.2" height="8.6" rx="2.6" />
              <path d="M3.8 8.4a5.2 5.2 0 0 0 10.4 0M9 13.6v2.4M6.2 16h5.6" />
            </svg>
            <span className="am-label">Recordings</span>
          </Link>
          )}

          <div className="am-sep" />

          <button type="button" className="am-item is-signout"
                  onClick={() => {
                    // The icon's number goes first: the page is about to be
                    // left, and a Dock badge outlives the page that set it.
                    resetBadge();
                    return onSignOut ? onSignOut() : signOut();
                  }}>
            <svg className="am-ico" viewBox="0 0 18 18" fill="none" stroke="currentColor"
                 strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M7 15.4H3.6a1.2 1.2 0 0 1-1.2-1.2V3.8a1.2 1.2 0 0 1 1.2-1.2H7" />
              <path d="M11.6 12.2 15.4 9l-3.8-3.2M15.4 9H6.6" />
            </svg>
            <span className="am-label">Sign out</span>
          </button>
        </div>
      )}

      {editingPhoto && <PhotoEditor name={me?.name || me?.email} onClose={() => setEditingPhoto(false)} />}
    </div>
  );
}
